import { useState, useEffect, useCallback } from 'react';
import type { CoinData } from '../types/interfaces';

const API_BASE = 'https://api.coingecko.com/api/v3';
const CACHE_VERSION = '2.0';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes - longer cache to avoid rate limits

export function useCryptoData(currency: string = 'usd') {
  const [cryptoData, setCryptoData] = useState<CoinData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWithTimeout = async (url: string, timeout: number = 15000): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof Error && err.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      throw err;
    }
  };

  const loadMarketData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let allData: CoinData[] = [];
      const storedCacheVersion = localStorage.getItem('cryptoMarketDataCacheVersion');
      
      // Check cache
      const cachedData = localStorage.getItem('cryptoMarketDataCache');
      const cacheTime = localStorage.getItem('cryptoMarketDataCacheTime');
      const cacheCurrency = localStorage.getItem('cryptoMarketDataCurrency');
      const now = Date.now();
      
      // Only invalidate cache if version changed (not currency - we'll try to fetch new currency but keep old as fallback)
      if (storedCacheVersion !== CACHE_VERSION) {
        localStorage.removeItem('cryptoMarketDataCache');
        localStorage.removeItem('cryptoMarketDataCacheTime');
        localStorage.setItem('cryptoMarketDataCacheVersion', CACHE_VERSION);
      }
      
      // Check for cached data - use it if available and for the same currency
      let useCacheOnly = false;
      if (cachedData && cacheTime && storedCacheVersion === CACHE_VERSION && cacheCurrency === currency) {
        try {
          const cacheAge = now - parseInt(cacheTime);
          allData = JSON.parse(cachedData);
          
          // Use cached data immediately
          setCryptoData(allData);
          setLoading(false);
          
          // If cache is fresh (less than 5 minutes old), use it and return
          if (cacheAge < CACHE_DURATION) {
            return; // Don't fetch new data, cache is fresh enough
          }
          
          // Cache exists but is old - we'll try to refresh, but keep showing cached data
        } catch (e) {
          // Cache is corrupted, clear it
          localStorage.removeItem('cryptoMarketDataCache');
          localStorage.removeItem('cryptoMarketDataCacheTime');
          allData = [];
        }
      } else if (cachedData && cacheTime && storedCacheVersion === CACHE_VERSION) {
        // We have cached data but for different currency - keep it as fallback
        try {
          allData = JSON.parse(cachedData);
          // Show it immediately as fallback, but still try to fetch new currency
          setCryptoData(allData);
          setLoading(false);
        } catch (e) {
          // Cache is corrupted
          allData = [];
        }
      }

      const targetCoins = 500;
      const coinsPerPage = 250;
      const pagesNeeded = Math.ceil(targetCoins / coinsPerPage);

      for (let page = 1; page <= pagesNeeded; page++) {
        try {
          const url = `${API_BASE}/coins/markets?vs_currency=${currency}&order=market_cap_desc&per_page=${coinsPerPage}&page=${page}&sparkline=false`;
          const response = await fetchWithTimeout(url, 15000);
          
          if (!response.ok) {
            if (response.status === 429) {
              const retryAfter = response.headers.get('Retry-After');
              const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : 60000;
              await new Promise(resolve => setTimeout(resolve, waitTime));
              page--;
              continue;
            }
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          const responseData = await response.json();
          
          if (responseData.error) {
            throw new Error(responseData.error);
          }
          
          if (!Array.isArray(responseData)) {
            throw new Error('Invalid response format from API');
          }
          
          if (responseData.length > 0) {
            // Add coins, ensuring no duplicates by ID
            responseData.forEach((coin: CoinData) => {
              if (!allData.find(c => c.id === coin.id)) {
                allData.push(coin);
              }
            });
            
            if (responseData.length < coinsPerPage || allData.length >= targetCoins) {
              break;
            }
            
            if (page < pagesNeeded && allData.length < targetCoins) {
              await new Promise(resolve => setTimeout(resolve, 3000));
            }
            
            localStorage.setItem('cryptoMarketDataCache', JSON.stringify(allData));
            localStorage.setItem('cryptoMarketDataCacheTime', Date.now().toString());
            localStorage.setItem('cryptoMarketDataCacheVersion', CACHE_VERSION);
            localStorage.setItem('cryptoMarketDataCurrency', currency);
          } else {
            break;
          }
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : String(err);
          const errorString = String(err).toLowerCase();
          
          // Handle rate limiting and CORS errors gracefully
          const isCorsOrRateLimit = errorMsg.includes('429') || 
                                    errorMsg.includes('rate limit') || 
                                    errorMsg.includes('cors') || 
                                    errorMsg.includes('Failed to fetch') ||
                                    errorString.includes('cors') ||
                                    errorString.includes('failed to fetch') ||
                                    err instanceof TypeError; // CORS often throws TypeError
          
          if (isCorsOrRateLimit) {
            // If we have cached data, use it
            if (allData.length > 0) {
              setCryptoData(allData);
              setLoading(false);
              setError('Using cached data. API rate limit or CORS issue. Please try again later.');
              return;
            }
            
            // If no cached data and first page fails, show error and use any existing cache (even from different currency)
            if (page === 1 && allData.length === 0) {
              // Try to find any cached data, even if it's for a different currency
              const staleCache = localStorage.getItem('cryptoMarketDataCache');
              if (staleCache) {
                try {
                  const staleData = JSON.parse(staleCache);
                  if (staleData && staleData.length > 0) {
                    setCryptoData(staleData);
                    setLoading(false);
                    setError('Using cached data (may be from different currency). API unavailable. Please refresh in a few minutes.');
                    return;
                  }
                } catch {
                  // Cache is invalid
                }
              }
              
              setLoading(false);
              setError('API unavailable (CORS/Rate limit). Please wait a few minutes and refresh the page.');
              return;
            }
            
            // If we have some data, use it
            if (allData.length > 0) {
              break;
            }
          }
          
          // For other errors, try to continue or fail gracefully
          if (allData.length === 0 && page === 1) {
            // Try to use stale cache first
            const staleCache = localStorage.getItem('cryptoMarketDataCache');
            if (staleCache) {
              try {
                const staleData = JSON.parse(staleCache);
                if (staleData && staleData.length > 0) {
                  setCryptoData(staleData);
                  setLoading(false);
                  setError(`Failed to load fresh data: ${errorMsg}. Using cached data.`);
                  return;
                }
              } catch {
                // Cache is invalid
              }
            }
            
            setLoading(false);
            setError(`Failed to load data: ${errorMsg}.`);
            return;
          } else if (allData.length > 0) {
            // We have some data, use it
            break;
          } else {
            throw err;
          }
        }
      }

      // Deduplicate data by coin ID before finalizing
      const uniqueData = allData.reduce((acc, coin) => {
        if (!acc.find(c => c.id === coin.id)) {
          acc.push(coin);
        }
        return acc;
      }, [] as CoinData[]);

      // Ensure BSV is included
      try {
        const hasBSV = uniqueData.some(coin => coin.id === 'bitcoin-sv' || coin.symbol.toLowerCase() === 'bsv');
        if (!hasBSV) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          const bsvUrl = `${API_BASE}/coins/markets?vs_currency=${currency}&ids=bitcoin-sv&sparkline=false`;
          const bsvResponse = await fetchWithTimeout(bsvUrl, 10000);
          if (bsvResponse.ok) {
            const bsvData = await bsvResponse.json();
            if (Array.isArray(bsvData) && bsvData.length > 0) {
              const bsvCoin = bsvData[0];
              // Only add if not already present
              if (!uniqueData.find(c => c.id === bsvCoin.id)) {
                uniqueData.push(bsvCoin);
              }
              uniqueData.sort((a, b) => (b.market_cap || 0) - (a.market_cap || 0));
            }
          }
        }
      } catch (bsvError) {
        // Continue without BSV
      }

      // Final deduplication before setting state
      const finalUniqueData = uniqueData.reduce((acc: CoinData[], coin: CoinData) => {
        if (!acc.find(c => c.id === coin.id)) {
          acc.push(coin);
        }
        return acc;
      }, []);
      
      setCryptoData(finalUniqueData);
      setLoading(false);
    } catch (err) {
      // Final error handling - try to use any cached data
      const staleCache = localStorage.getItem('cryptoMarketDataCache');
      if (staleCache) {
        try {
          const staleData = JSON.parse(staleCache);
          setCryptoData(staleData);
          setError('Using cached data. Unable to fetch fresh data due to rate limits.');
        } catch {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load cryptocurrency data';
          setError(errorMessage);
        }
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load cryptocurrency data';
        setError(errorMessage);
      }
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMarketData();
  }, [loadMarketData]);

  const refresh = useCallback(() => {
    localStorage.removeItem('cryptoMarketDataCache');
    localStorage.removeItem('cryptoMarketDataCacheTime');
    loadMarketData();
  }, [loadMarketData]);

  return { cryptoData, loading, error, refresh };
}

